import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { formatAge, isUrgentOpen } from "@/lib/ticketUtils";
import { CATEGORIES } from "@/lib/constants";
import {
  Inbox,
  AlertOctagon,
  Activity,
  CheckCircle2,
  Building2,
  Tent,
  Tag,
  MapPin,
  Loader2,
  TrendingUp,
  Flame,
  PieChart,
} from "lucide-react";

const TONE = {
  blue: "bg-[color:var(--enka-blue-50)] text-[color:var(--enka-blue)]",
  red: "bg-red-50 text-red-600",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  slate: "bg-slate-100 text-slate-700",
};

const StatCard = ({ icon: Icon, label, value, tone = "blue", testid, hint }) => (
  <Card className="p-5 transition-shadow hover:shadow-md" data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${TONE[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-[color:var(--enka-muted)] uppercase tracking-wider">
          {label}
        </div>
        <div className="text-3xl font-display text-[color:var(--enka-ink)] mt-1 leading-none truncate">
          {value ?? "—"}
        </div>
        {hint ? (
          <div className="text-xs text-[color:var(--enka-muted)] mt-1">{hint}</div>
        ) : null}
      </div>
    </div>
  </Card>
);

const TwoColorBar = ({ aLabel, aValue, bLabel, bValue, aColor, bColor }) => {
  const total = (aValue || 0) + (bValue || 0);
  const aPct = total ? Math.round((aValue / total) * 100) : 0;
  const bPct = total ? 100 - aPct : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-[color:var(--enka-muted)] mb-2">
        <span>
          <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: aColor }} />
          {aLabel} <strong className="text-[color:var(--enka-ink)]">{aValue}</strong> ({aPct}%)
        </span>
        <span>
          <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: bColor }} />
          {bLabel} <strong className="text-[color:var(--enka-ink)]">{bValue}</strong> ({bPct}%)
        </span>
      </div>
      <div className="h-3 rounded-full overflow-hidden bg-slate-100 flex">
        <div className="h-full transition-all" style={{ width: `${aPct}%`, background: aColor }} />
        <div className="h-full transition-all" style={{ width: `${bPct}%`, background: bColor }} />
      </div>
    </div>
  );
};

const CategoryBars = ({ byCategory }) => {
  const max = Math.max(1, ...Object.values(byCategory || {}));
  return (
    <div className="space-y-2.5" data-testid="dash-by-category">
      {CATEGORIES.map((c) => {
        const v = byCategory?.[c] || 0;
        const pct = Math.round((v / max) * 100);
        return (
          <div key={c}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[color:var(--enka-ink)] truncate pr-3">{c}</span>
              <span className="font-mono font-semibold text-[color:var(--enka-blue)]">{v}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-[color:var(--enka-blue)] transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [urgent, setUrgent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/tickets", { params: { priority: "Urgent" } }),
    ])
      .then(([s, t]) => {
        setStats(s.data);
        setUrgent(t.data.filter(isUrgentOpen).slice(0, 6));
      })
      .finally(() => setLoading(false));
  }, []);

  const totalsForBar = useMemo(
    () => ({ site: stats?.site || 0, camp: stats?.camp || 0 }),
    [stats]
  );

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--enka-muted)] font-semibold">
            IQ3B9
          </div>
          <h1 className="font-display text-3xl text-[color:var(--enka-ink)] mt-1">Dashboard</h1>
          <p className="text-sm text-[color:var(--enka-muted)]">
            Real-time overview of personnel feedback tickets.
          </p>
        </div>
        <Link
          to="/admin/tickets"
          className="text-sm font-medium text-[color:var(--enka-blue)] hover:underline"
          data-testid="dashboard-view-tickets-link"
        >
          Go to all tickets &rarr;
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
        </div>
      ) : (
        <>
          {/* Top KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-total" icon={Inbox} label="Total Tickets" value={stats.total} tone="blue" />
            <StatCard testid="stat-open" icon={Activity} label="Open Tickets" value={stats.open} tone="amber" />
            <StatCard
              testid="stat-urgent"
              icon={AlertOctagon}
              label="Urgent Tickets"
              value={stats.urgent}
              tone="red"
              hint={`${stats.urgent_open ?? 0} still open`}
            />
            <StatCard testid="stat-in-progress" icon={TrendingUp} label="In Progress" value={stats.in_progress} tone="amber" />
            <StatCard testid="stat-resolved" icon={CheckCircle2} label="Resolved" value={stats.resolved} tone="emerald" />
            <StatCard testid="stat-site" icon={Building2} label="Site Tickets" value={stats.site} tone="blue" />
            <StatCard testid="stat-camp" icon={Tent} label="Camp Tickets" value={stats.camp} tone="blue" />
            <StatCard
              testid="stat-most-category"
              icon={Tag}
              label="Most Reported Category"
              value={stats.most_reported_category}
              tone="slate"
            />
          </div>

          {/* Second row: most-location + status breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <StatCard
              testid="stat-most-location"
              icon={MapPin}
              label="Most Reported Location"
              value={stats.most_reported_location}
              tone="slate"
            />
            <Card className="p-5 md:col-span-2" data-testid="stat-status-breakdown">
              <div className="text-[11px] font-semibold text-[color:var(--enka-muted)] uppercase tracking-wider mb-3">
                Tickets by status
              </div>
              <div className="grid grid-cols-5 gap-3">
                {Object.entries(stats.by_status).map(([k, v]) => (
                  <div key={k} className="text-center">
                    <div className="text-2xl font-display text-[color:var(--enka-ink)]">{v}</div>
                    <div className="text-[11px] text-[color:var(--enka-muted)] mt-0.5">{k}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Visual sections */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card className="p-6" data-testid="dash-area-distribution">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="h-4 w-4 text-[color:var(--enka-blue)]" />
                <h3 className="font-display text-base text-[color:var(--enka-ink)]">Site vs Camp</h3>
              </div>
              <TwoColorBar
                aLabel="Site"
                aValue={totalsForBar.site}
                bLabel="Camp"
                bValue={totalsForBar.camp}
                aColor="var(--enka-blue)"
                bColor="#5b8def"
              />
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2 text-[color:var(--enka-muted)] text-xs">
                    <Building2 className="h-3.5 w-3.5" /> Site
                  </div>
                  <div className="font-display text-xl text-[color:var(--enka-ink)]">{totalsForBar.site}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2 text-[color:var(--enka-muted)] text-xs">
                    <Tent className="h-3.5 w-3.5" /> Camp
                  </div>
                  <div className="font-display text-xl text-[color:var(--enka-ink)]">{totalsForBar.camp}</div>
                </div>
              </div>
            </Card>

            <Card className="p-6" data-testid="dash-tickets-by-category">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="h-4 w-4 text-[color:var(--enka-blue)]" />
                <h3 className="font-display text-base text-[color:var(--enka-ink)]">Tickets by category</h3>
              </div>
              <CategoryBars byCategory={stats.by_category} />
            </Card>

            <Card className="p-6" data-testid="dash-urgent-focus">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="h-4 w-4 text-red-600" />
                <h3 className="font-display text-base text-[color:var(--enka-ink)]">Urgent focus</h3>
              </div>
              <p className="text-xs text-[color:var(--enka-muted)] mb-3">
                Open urgent tickets needing attention.
              </p>
              {urgent.length === 0 ? (
                <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/40 text-emerald-700 text-sm p-4 text-center">
                  No urgent open tickets. 
                </div>
              ) : (
                <ul className="space-y-2">
                  {urgent.map((t) => (
                    <li
                      key={t.id}
                      data-testid={`urgent-focus-item-${t.ticket_no}`}
                      className="flex items-center gap-3 rounded-lg border border-red-100 bg-red-50/40 px-3 py-2"
                    >
                      <Link
                        to={`/admin/tickets?open=${t.id}`}
                        className="font-mono text-sm font-semibold text-[color:var(--enka-blue)] hover:underline shrink-0"
                      >
                        {t.ticket_no}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[color:var(--enka-ink)] truncate">
                          {t.area} · {t.category}
                        </div>
                        <div className="text-[11px] text-[color:var(--enka-muted)] truncate">
                          {t.location}
                        </div>
                      </div>
                      <div className="text-[11px] text-red-700 font-semibold whitespace-nowrap">
                        {formatAge(t.created_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
